import React, { useCallback, useMemo } from 'react'
import NavigateNextIcon from '@material-symbols/svg-300/rounded/navigate_next.svg'
import NavigateBeforeIcon from '@material-symbols/svg-300/rounded/navigate_before.svg'

import Button from '../basics/Button/Button'

import styles from './Pagination.module.scss'

function calculatePageLinks(page: number, pageCount: number) {
  const startLinks = 6

  const b: (number | undefined)[] = [...Array(Math.min(startLinks + 1, pageCount)).keys()]
  for (let i = Math.max(b[b.length - 1]! + 1, pageCount - startLinks); i < pageCount; i += 1) {
    b.push(i)
  }

  if (pageCount > 2 * startLinks + 1) {
    b[startLinks] = undefined

    let idx = b.findIndex((n) => n === page)
    if (idx === -1) {
      idx = startLinks
    }

    b[idx] = page

    if (page > startLinks && page < pageCount - startLinks / 2) {
      b[idx - 1] = page - 1
      b[idx - 2] = page - 2
      b[idx - 3] = undefined
    }
    if (page < pageCount - startLinks - 1 && page > startLinks / 2) {
      b[idx + 1] = page + 1
      b[idx + 2] = page + 2
      b[idx + 3] = undefined
    }
  }

  return b
}

interface PaginationProps {
  page: number
  pageCount: number
  pageSize: number
  itemCount: number
  onPageChanged: (page: number) => void
}

export default React.memo(function Pagination({
  page,
  pageCount,
  pageSize,
  itemCount,
  onPageChanged,
}: PaginationProps) {
  const pageLinks = useMemo(() => calculatePageLinks(page, pageCount), [page, pageCount])

  const handleChangePage = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.preventDefault()
      onPageChanged(parseInt(e.currentTarget.dataset.pageNumber || '') || 0)
    },
    [onPageChanged]
  )

  return (
    <div className={styles.Pagination}>
      {pageLinks.length > 1 && (
        <>
          <Button key="prev" small data-page-number={page - 1} onClick={handleChangePage} disabled={page <= 0}>
            <NavigateBeforeIcon />
          </Button>
          {pageLinks.map((p, idx) =>
            p !== undefined ? (
              <Button key={idx} small filled={p === page} data-page-number={p} onClick={handleChangePage}>
                {p + 1}
              </Button>
            ) : (
              <span key={idx} className={styles.placeholder}>
                ...
              </span>
            )
          )}
          <Button
            key="next"
            small
            data-page-number={page + 1}
            onClick={handleChangePage}
            disabled={page + 1 >= pageCount}
          >
            <NavigateNextIcon />
          </Button>
        </>
      )}
      <div className={styles.totals}>
        {pageSize * page + 1} - {Math.min(pageSize * (page + 1), itemCount)} / {itemCount}
      </div>
    </div>
  )
})
